import Web3 from 'web3';
import foreignAbi from './abi/foreign-bridge.json';
import homeAbi from './abi/home-bridge.json';
import Config from '../../config';

const strip0x = (input) => {
  return input.replace(/^0x/, '');
};

const errors = {
  signatureNotReady: 'error_signature_not_ready',
  moreSignaturesRequired: 'error_more_signatures_required',
  invalidTransactionHash: 'error_invalid_transaction_hash',
};
class Claim {
  constructor(blockv) {
    this.errors = errors;
    this.blockv = blockv;
    this.web3 = blockv.web3;
    this.homeBridge = new this.web3.eth.Contract(homeAbi.abi, Config.withdrawAddress);
    this.foreignBridge = new this.web3.eth.Contract(foreignAbi.abi, Config.depositAddress);
  }

  getTransactionMessageHash(hash, bridge) {
    return this.web3.eth.getTransaction(hash).then((transaction) => {
      if (!transaction) {
        const error = new Error('Invalid transaction hash');
        error.code = errors.invalidTransactionHash;
        throw error;
      }
      console.log(transaction);
      return Web3.utils.soliditySha3(
        this.createMessage({
          recipient: transaction.from,
          value: transaction.value,
          transactionHash: hash,
          bridgeAddress: bridge,
        }),
      );
    });
  }

  createMessage({ recipient, value, transactionHash, bridgeAddress }) {
    let messageValue = Web3.utils.numberToHex(value);
    messageValue = Web3.utils.padLeft(messageValue, 32 * 2);
    messageValue = strip0x(messageValue);

    const message = `0x${strip0x(recipient)}${messageValue}${strip0x(transactionHash)}${strip0x(
      bridgeAddress,
    )}`;
    return message;
  }

  getNumberMessagesSigned(hash) {
    return this.homeBridge.methods
      .numMessagesSigned(hash)
      .call()
      .then((number) => {
        return Web3.utils
          .toBN(number)
          .and(Web3.utils.toBN('0x8fffffffffffffffffffffffffffffffffffffffffff'))
          .toNumber();
      });
  }

  makeSignature(hash, number) {
    const signatures = [];
    for (let i = 0; i < number; i += 1) {
      signatures.push(
        this.homeBridge.methods
          .signature(hash, i)
          .call()
          .then((signature) => this.signatureToVRS(signature)),
      );
    }
    return Promise.all(signatures).then((result) => this.packSignatures(result));
  }

  signatureToVRS(rawSignature) {
    const signature = strip0x(rawSignature);
    const v = signature.substr(64 * 2);
    const r = signature.substr(0, 32 * 2);
    const s = signature.substr(32 * 2, 32 * 2);
    return { v, r, s };
  }

  packSignatures(array) {
    const length = strip0x(Web3.utils.toHex(array.length));
    const msgLength = length.length === 1 ? `0${length}` : length;
    let v = '';
    let r = '';
    let s = '';
    array.forEach((e) => {
      v = v.concat(e.v);
      r = r.concat(e.r);
      s = s.concat(e.s);
    });
    return `0x${msgLength}${v}${r}${s}`;
  }

  createMessageSignature(transaction, waitForMessage) {
    return this.getTransactionMessageHash(transaction, Config.depositAddress)
      .then((messageHash) => {
        return new Promise((resolve, reject) => {
          const getMessage = () => {
            this.homeBridge.methods
              .message(messageHash)
              .call()
              .then((message) => {
                if (message || !waitForMessage) {
                  resolve(message);
                } else {
                  setTimeout(() => {
                    getMessage();
                  }, 5000);
                }
              })
              .catch((error) => {
                reject(error);
              });
          };
          getMessage();
        }).then((message) => ({ message, messageHash }));
      })
      .then(({ message, messageHash }) => {
        if (!message) {
          const error = new Error('signatures not ready, try again later');
          error.code = errors.signatureNotReady;
          throw error;
        }
        return this.homeBridge.methods
          .requiredSignatures()
          .call()
          .then((required) => {
            return new Promise((resolve, reject) => {
              const checkNumSigned = () => {
                this.getNumberMessagesSigned(messageHash)
                  .then((signed) => {
                    if (signed >= required) {
                      resolve({ required, signed });
                    } else {
                      setTimeout(() => {
                        checkNumSigned();
                      }, 3000);
                    }
                  })
                  .catch((error) => reject(error));
              };
              checkNumSigned();
            });
          })
          .then(({ required, signed }) => {
            if (signed < required) {
              const error = new Error(`${required} signature required`);
              error.code = errors.moreSignaturesRequired;
              throw error;
            }
            return this.makeSignature(messageHash, required).then((signature) => ({
              message,
              signature,
            }));
          });
      });
  }

  claim(message, signature, onTransactionHash) {
    return new Promise((resolved, reject) => {
      this.foreignBridge.methods
        .executeSignatures(message, signature)
        .send({
          from: this.blockv.account,
        })
        .on('transactionHash', (hash) => {
          onTransactionHash?.(hash);
        })
        .on('receipt', (receipt) => {
          resolved(receipt);
        })
        .on('error', (error) => {
          reject(error);
        });
    });
  }
}

export default Claim;
